import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import CouponModal from '../discounts/couponDiscount';
import AdditionalDiscountModal from '../discounts/addtionalDiscount';
import TipModal from '../discounts/tip';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addAdditionalCharge, addAdditionalDiscount, addManualDiscount, addTip } from '../../../redux/slices/newSellscreenSlice';
import CommonService from '../../../shared/services/commonService';

type ActionType = 'coupon' | 'discount' | 'charge' | 'tip';

export default function ActionSection() {
	const dispatch = useAppDispatch()
	const { additionalDiscount, manualDiscount, additionalCharge, tip, automaticDiscount, sellScreenType } = useAppSelector((state) => state.NewSellscreenSlice);
	const locationId = useAppSelector((state) => state?.common?.currentLocation?.id)

	const [modalVisible, setModalVisible] = useState<{
		coupon?: boolean,
		discount?: boolean,
		charge?: boolean,
		tip?: boolean
	}>({});
	const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);

	useEffect(() => {
		if (selectedAction) {
			switch (selectedAction) {
				case 'coupon':
					setModalVisible({ coupon: true })
					break;
				case 'discount':
					setModalVisible({ discount: true })
					break;
				case 'charge':
					setModalVisible({ charge: true })
					break;
				case 'tip':
					setModalVisible({ tip: true })
					break;
			}
		}
	}, [selectedAction])

	const handlePress = (type: ActionType) => {
		setSelectedAction(type);
		setModalVisible({});
	};

	const handleRemove = (type: ActionType) => {
		switch (type) {
			case 'coupon':
				dispatch(addManualDiscount({ discount: null, locationId }))
				break;
			case 'discount':
				dispatch(addAdditionalDiscount({ discount: null, locationId }))
				break;
			case 'charge':
				dispatch(addAdditionalCharge({ charge: null, locationId }))
				break;
			case 'tip':
				dispatch(addTip({ tip: null, locationId }))
				break;
		}
		setSelectedAction(null);
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={{ padding: 16, backgroundColor: theme?.light }}>
						<View style={styles.actionsGrid}>
							<VerticalActionPill
								sellScreenType={sellScreenType}
								label="Coupon"
								icon="🏷️"
								color={theme?.couponDis}
								value={manualDiscount?.[locationId]?.isValidate ? manualDiscount?.[locationId]?.discountAmount?.toString() : ""}
								code={manualDiscount?.[locationId]?.isValidate ? manualDiscount?.[locationId]?.promoCode : ""}
								onPress={() => handlePress('coupon')}
								onRemove={() => handleRemove('coupon')}
								disabled={(additionalDiscount?.[locationId] || automaticDiscount[locationId]) ? true : false}
							/>
							<VerticalActionPill
								sellScreenType={sellScreenType}
								label="Discount"
								icon="％"
								color={theme?.additionalDis}
								value={additionalDiscount?.[locationId] ? additionalDiscount?.[locationId]?.discountAmount.toString() : ""}
								onPress={() => handlePress('discount')}
								onRemove={() => handleRemove('discount')}
								disabled={(manualDiscount?.[locationId] || automaticDiscount[locationId]) ? true : false}
							/>
							<VerticalActionPill
								sellScreenType={sellScreenType}
								label="Charge"
								icon="⚡"
								color={theme?.addCharge}
								value={additionalCharge?.[locationId]?.amount ? additionalCharge?.[locationId]?.amount?.toString() : ""}
								onPress={() => handlePress('charge')}
								onRemove={() => handleRemove('charge')}
							/>
							<VerticalActionPill
								sellScreenType={sellScreenType}
								label="Tip"
								icon="💝"
								color={theme?.tip}
								value={tip?.[locationId] ? tip?.[locationId]?.toString() : ""}
								onPress={() => handlePress('tip')}
								onRemove={() => handleRemove('tip')}
							/>
						</View>

						<CouponModal
							onClose={() => { setSelectedAction(null); setModalVisible({ coupon: false }) }}
							visible={modalVisible?.coupon || false}
						/>
						<AdditionalDiscountModal
							onClose={() => { setSelectedAction(null); setModalVisible({ discount: false }) }}
							visible={modalVisible?.discount || modalVisible?.charge || false}
							type={modalVisible?.discount ? 'discount' : 'charge'}
						/>
						<TipModal
							onClose={() => { setSelectedAction(null); setModalVisible({ tip: false }) }}
							visible={modalVisible?.tip || false}
						/>

					</View>
				)
			}}
		</ThemeConsumer>
	);
}

// --- UPDATED PILL COMPONENT ---
const VerticalActionPill = ({
	label, icon, color, value, onPress, onRemove, code, disabled, sellScreenType
}: {
	label: string, icon: string, color: string, value?: string, onPress: () => void, onRemove: () => void, code?: string, disabled?: boolean, sellScreenType: "SELL" | "RETURN" | "REPLACEMENT"
}) => {

	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const isActive = !!Number(value);

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<TouchableOpacity
						style={[
							styles.actionPill,
							isActive && {
								backgroundColor: color + '08',
								borderColor: color
							}, sellScreenType === "RETURN" && { opacity: 0.7 }
						]}
						onPress={() => {
							if (disabled) ZenToast({ type: "info", message: "You can apply only one coupon at a time" })
							else onPress()
						}}
						activeOpacity={0.7}
						disabled={sellScreenType != "SELL"}
					>
						{isActive && sellScreenType == "SELL" && (
							<TouchableOpacity
								style={styles.cleanCloseBtn}
								onPress={onRemove}
								hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
							>
								<ZenIcon name='close' type='antdesign' color={color} size={15} />
							</TouchableOpacity>
						)}

						<View style={[
							styles.actionIconBox,
							{ backgroundColor: isActive ? color : color + '15' }
						]}>
							<ZenText content={icon} style={[
								styles.actionIcon,
								{ color: isActive ? theme?.light : color }
							]} />
						</View>

						{isActive ? (
							<View style={styles.activeTextContainer}>
								<ZenText content={label} style={[styles.tinyLabel, { color }]} />
								<ZenText content={CommonService?.currencyStandard(currencySymbol, value?.toString() || "")} style={[styles.activeValue, { color }]} />
								{code ? <ZenText content={"CODE: " + code} style={[styles.tinyLabel, { color }]} /> : null}
							</View>
						) : (
							<ZenText content={label} style={styles.actionLabel} />
						)}

					</TouchableOpacity>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => (
	StyleSheet.create({
		actionsGrid: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			marginBottom: 10,
			gap: 8,
		},
		actionPill: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: theme?.iceWhite,
			paddingVertical: 10,
			borderRadius: 12,
			borderWidth: 1,
			borderColor: theme?.softSlate,
			minHeight: 85,
		},
		actionIconBox: {
			width: 32,
			height: 32,
			borderRadius: 16,
			alignItems: 'center',
			justifyContent: 'center',
			marginBottom: 6
		},
		actionIcon: {
			fontSize: theme?.smallHeaderFontSize,
			fontFamily: theme?.medium
		},

		// Default Label
		actionLabel: {
			fontSize: theme?.badgeFontSize,
			fontFamily: theme?.medium,
			color: theme?.grey
		},

		// --- ACTIVE TEXT STYLES ---
		activeTextContainer: {
			alignItems: 'center',
		},
		tinyLabel: {
			fontSize: theme?.miniBadge,
			fontFamily: theme?.bold,
			textTransform: 'uppercase',
			opacity: 0.8,
			marginBottom: 1,
		},
		activeValue: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.bold,
			letterSpacing: -0.5,
		},

		// Close Icon
		cleanCloseBtn: {
			position: 'absolute',
			top: 0,
			right: 0,
			padding: 10,
			zIndex: 10
		},
		closeIconChar: {
			fontSize: 10,
			fontWeight: '900',
			opacity: 0.6,
		},

		// Modal
		modalOverlay: { flex: 1, backgroundColor: theme?.modalBg300, justifyContent: 'center', alignItems: 'center' },
		modalCard: { width: 200, backgroundColor: theme?.light, padding: 20, borderRadius: 12, alignItems: 'center' },
		modalBtn: { backgroundColor: theme.primary, padding: 10, borderRadius: 6, width: '100%', alignItems: 'center' }
	})
)